import { InputUnstyled } from "@nova/ds-component-input"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useEffect, useRef, useState } from "react"
import { useDebounceDispatch } from "tools/store"
import {
    closeFullPanel,
    selectActiveSearchPanelPlugin,
    selectRecentSearchesPanelOpen,
    selectSearchQuery,
    setSearchInputFocused,
    setSearchQuery,
} from "../slice"
import { getActivatablePlugin, searchPlugins, SearchPluginName } from "./registry"
import { useScopedSearchResults, useSearchResults } from "./useSearchResults"
import { NovaSpinner } from "@nova/component-nova-spinner"

export const OverviewSearchBar = () => {
    const dispatch = useAppDispatch()
    const { debounceDispatch } = useDebounceDispatch(500)
    const searchQuery = useAppSelector(selectSearchQuery)
    const hasInflightDebouncedWrite = useRef(false)
    const [value, setValue] = useState(searchQuery)
    const recentSearchesPanelOpen = useAppSelector(selectRecentSearchesPanelOpen)
    const { isLoading: isLoadingSearchResults } = useSearchResults(searchQuery, {
        enabled: !recentSearchesPanelOpen,
    })
    const activatedPlugin = useAppSelector(selectActiveSearchPanelPlugin)
    const { isLoading: isLoadingScopedSearchResults } = useScopedSearchResults(
        activatedPlugin,
        searchQuery
    )
    const placeholder = recentSearchesPanelOpen
        ? "Search recents"
        : activatedPlugin
          ? (getActivatablePlugin(activatedPlugin).fullPanelSearchPlaceholder ??
            `Search for ${searchPlugins[activatedPlugin as SearchPluginName].displayName.toLowerCase()}`)
          : "Search for aircraft, projects, or people"

    const handleChange = (text: string) => {
        setValue(text)
        hasInflightDebouncedWrite.current = true
        debounceDispatch(setSearchQuery(text))
    }

    const handleClear = () => {
        setValue("")
        dispatch(setSearchQuery(""))
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== "Enter") return
        dispatch(setSearchInputFocused(false))
        if (activatedPlugin || recentSearchesPanelOpen) {
            dispatch(closeFullPanel())
        }
    }

    useEffect(() => {
        if (searchQuery === "" && !hasInflightDebouncedWrite.current) {
            setValue("")
        }
        hasInflightDebouncedWrite.current = false
    }, [searchQuery])

    return (
        <div className="pointer-events-auto">
            <InputUnstyled
                containerClassName="bg-black-800 backdrop-blur-sm border border-white-100 rounded-full h-[52px] w-[368px] pr-5"
                leftItem={
                    <Icons.Icon
                        Icon={Icons.MagnifyingGlass}
                        className="text-gray-500 group-hover/input:text-white group-focus-within/input:text-white"
                    />
                }
                rightItem={(() => {
                    if (
                        isLoadingSearchResults ||
                        isLoadingScopedSearchResults ||
                        value !== searchQuery
                    ) {
                        return (
                            <div className="w-6 h-6 flex justify-center items-center">
                                <NovaSpinner height={20} width={20} />
                            </div>
                        )
                    }
                    if (value) {
                        return (
                            <Icons.Icon
                                Icon={Icons.XClose}
                                className="text-gray-500 group-hover/input:text-gray-200 group-focus-within/input:text-gray-200 hover:text-white cursor-pointer"
                                onClick={handleClear}
                            />
                        )
                    }
                    return (
                        <Icons.Icon
                            Icon={Icons.XClose}
                            className="hidden group-focus-within/input:block text-gray-200 cursor-pointer"
                        />
                    )
                })()}
                placeholder={placeholder}
                value={value}
                onChange={(e) => handleChange(e.target.value)}
                onFocus={() => dispatch(setSearchInputFocused(true))}
                onKeyDown={handleKeyDown}
            />
        </div>
    )
}
